'use client'
import React, { useEffect, useState } from 'react'
import PageHeader from '../../components/Account/PageHeader'
import AccountWrapper from '../../components/Account/AccountWrapper'
import useDetectDevices from '../../hooks/useDetectDevices'
import { fetchStoreCreditBalance, fetchStoreCreditTransactions } from '../../utils/api'
import Divider from '../../components/common/Divider'
import QuinceLongLogo from '../../assets/quince_logo_long.svg'
import Collapsible from '../../components/Account/Collapsible'
import { ErrorMessage } from '../../components/FormElements'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { Link, useQuinceRouter as useRouter } from '@src/modules/navigation'
import styles from './my-store-credit.module.scss'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useTranslation } from 'next-i18next'
import { capitalizeAllWords, capitalizeWord } from '../../utils'
import { TOP_TIP_POSITION, ToolTipWrapper } from '@src/quince-components/src/atoms/tooltip/Tooltip'
import Info from '@src/quince-site-integrated/src/assets/info_outline.svg'
import { DateTime, STANDARD_DATE_OVERRIDES } from '@src/blocks/date-time'

// Constants to eliminate magic strings
const LINK_TARGET_BLANK = '_blank'
const LINK_REL_NOOPENER = 'noopener noreferrer'

const MERCHANT_ID_VS_MERCHANT_CUSTOM_NAME_MAP = {
  // @i18n-ignore
  448542: 'Doordash',
}

function getOrderLink(transaction) {
  return (
    <Link
      href={`/account/my-orders-returns?orderNumber=${transaction?.detail?.orderNumber}`}
      className={styles.orderLink}
    >
      {`#${transaction?.detail?.orderNumber}`}
    </Link>
  )
}

function getTransactionValue(transaction) {
  return <>{`${transaction?.type === 'CREDIT' ? '+' : '-'}$${Math.abs(parseFloat(transaction?.amount)).toFixed(2)}`}</>
}

function getTransactionTime(transaction) {
  return (
    <DateTime
      date={transaction?.transactionTime}
      formatName="SHORT_MONTH_DAY_YEAR"
      overrides={STANDARD_DATE_OVERRIDES.STORE_CREDITS}
    />
  )
}

const renderTooltip = () => {
  const { t } = useTranslation('common')
  return (
    <ToolTipWrapper
      toolTipContentClassName={styles.desktopTooltip}
      topTipPosition={TOP_TIP_POSITION.BOTTOM}
      toolTipContentRenderer={() => (
        <div>
          {t('store_credit_refund_adjustment_tooltip')}{' '}
          <Link
            href="/shipping-returns"
            target={LINK_TARGET_BLANK}
            rel={LINK_REL_NOOPENER}
            className={styles.learnMoreText}
            aria-label={t('free_shipping_easy_return_d3yff')}
          >
            Learn more
          </Link>
        </div>
      )}
    >
      <Info className={styles.infoIcon} />
    </ToolTipWrapper>
  )
}

const MobileTooltip = ({ t, transactionId, onToggle, isVisible }) => {
  return (
    <div className={styles.mobileTooltipContainer}>
      <Info className={styles.infoIcon} onClick={() => onToggle(transactionId)} />
      {isVisible && (
        <div className={styles.mobileTooltip}>
          {t('store_credit_refund_adjustment_tooltip')}{' '}
          <Link
            href="/shipping-returns"
            target={LINK_TARGET_BLANK}
            rel={LINK_REL_NOOPENER}
            className={styles.learnMoreText}
            aria-label={t('free_shipping_easy_return_d3yff')}
          >
            Learn more
          </Link>
          <div className={styles.mobileTooltipPin} />
        </div>
      )}
    </div>
  )
}

function MobileTransactions({ mobileTransactions }) {
  const { t } = useTranslation('common')
  const [visibleTooltips, setVisibleTooltips] = useState({})

  const toggleTooltip = transactionId => {
    setVisibleTooltips(prev => ({
      ...prev,
      [transactionId]: !prev[transactionId],
    }))
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (!event.target.closest('.tooltip') && !event.target.closest('[class*="infoIcon"]')) {
        setVisibleTooltips({})
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return mobileTransactions.map(transaction => (
    <div className={styles.detailContainer} key={transaction?.id}>
      <p className={styles.transactionDetail}>
        <span>
          {t(transaction.description.split('#')[0])} <br />
          {transaction.description.split('#')[1] !== undefined ? getOrderLink(transaction) : ''}
        </span>
        {transaction.reasonCode === 'ADJUSTMENT_ORDER_PAYMENT' && (
          <span className={styles.info}>
            <MobileTooltip
              t={t}
              transactionId={transaction?.id}
              onToggle={toggleTooltip}
              isVisible={visibleTooltips[transaction?.id]}
            />
          </span>
        )}
      </p>
      <div className={styles.rightContainerMobileTransactions}>
        <p className={styles.transactionValue}>{getTransactionValue(transaction)}</p>
        <p className={styles.transactionTime}>{getTransactionTime(transaction)}</p>
      </div>
    </div>
  ))
}

function DesktopTransactions({ transactions }) {
  const { t } = useTranslation('accountPage')
  const { t: tCommon } = useTranslation('common')

  return (
    <div className={styles.desktopScrollableContainer}>
      <div className={styles.detailContainer} noMargin>
        <p className={styles.tableHeaderDesktop}>{capitalizeAllWords(t('date'))} </p>
        <p className={styles.tableHeaderDesktop}>{capitalizeAllWords(t('description'))}</p>
        <p className={styles.tableHeaderDesktop}>{capitalizeAllWords(t('amount'))}</p>
      </div>
      {transactions.map(transaction => (
        <div className={styles.detailContainer} key={transaction?.id}>
          <p className={styles.transactionTime}>{getTransactionTime(transaction)}</p>
          <p className={styles.transactionDetail}>
            {t(transaction.description.split('#')[0])}
            {transaction.description.split('#')[1] !== undefined ? getOrderLink(transaction) : ''}
            {transaction.reasonCode === 'ADJUSTMENT_ORDER_PAYMENT' && (
              <span className={styles.info}>{renderTooltip()}</span>
            )}
          </p>
          <p className={styles.transactionValue}>{getTransactionValue(transaction)}</p>
        </div>
      ))}
    </div>
  )
}

const showActivity = (transactions, mobileTransactions) => {
  return (transactions ?? []).length > 0 || (mobileTransactions ?? []).length > 0
}

function MyStoreCredit({
  data: {
    accountPage: { contact, hero, menuList, slug, text, title },
  },
}) {
  const { t } = useTranslation('accountPage')
  const { t: tCommon } = useTranslation('common')
  const router = useRouter()
  const [balance, setBalance] = useState(0)
  const [transactions, setTransactions] = useState([])
  const [mobileTransactions, setMobileTransactions] = useState([])

  const { devices } = useDetectDevices()

  useEffect(() => {
    if (!AuthUtil.isLoggedIn() && !getIsNativeViewClient()) {
      const currentUrl = window.location.pathname
      router.push(`/log-in?redirecturi=${currentUrl}`)
      return
    }
    fetchStoreCreditTransactions().then(setTransactions)
    fetchStoreCreditBalance().then(setBalance)
  }, [])

  useEffect(() => {
    setMobileTransactions(transactions.slice(0, 6))
  }, [transactions])

  const isError = balance < 0

  return (
    <AccountWrapper heroImage={hero} menu={menuList} contact={contact}>
      <PageHeader title={title} />
      <div className={styles.container}>
        <div className={styles.styledDivider}>
          <Divider />
        </div>
        {text ? <p className={styles.text}>{text}</p> : null}
        <div className={styles.storeCreditBalanceCard}>
          <p className={styles.title}>{capitalizeWord(t('storeCreditBal'))} </p>

          <QuinceLongLogo className={styles.styledLongLogo} />
          <p className={styles.value}>
            {t('currency')}
            <span>{!isError ? parseFloat(balance).toFixed(2) : ''}</span>
          </p>
        </div>
        {isError && <ErrorMessage>{t('storeCreditBal')}</ErrorMessage>}

        {showActivity(transactions, mobileTransactions) && (
          <>
            <div className={styles.styledDividerActivityTop}>
              <Divider />
            </div>
            <div className={styles.styledCollapsible}>
              <Collapsible title={tCommon('activity')}>
                {(transactions ?? []).length > 0 && (
                  <div className={styles.activityContainer}>
                    {devices.mobile && <MobileTransactions mobileTransactions={mobileTransactions} />}
                    {devices.desktop && <DesktopTransactions transactions={transactions} />}
                  </div>
                )}
                {transactions.length !== mobileTransactions.length && devices.mobile && (
                  <div
                    className={styles.styledButton}
                    onClick={() => {
                      setMobileTransactions(transactions)
                    }}
                  >
                    {t('seeMore', { action: 'Store Credit Activity' })}
                  </div>
                )}
              </Collapsible>
            </div>
            <div className={styles.styledDividerActivityBottom}>
              <Divider />
            </div>
          </>
        )}
      </div>
    </AccountWrapper>
  )
}

export default MyStoreCredit
